import React, { useEffect, useState, useMemo } from "react";
import {
    collection,
    onSnapshot,
    doc,
    updateDoc,
    deleteDoc,
    getDoc,
    serverTimestamp,
    addDoc,
    query,
    where,
    getDocs,
} from "firebase/firestore";
import { db } from "../firebase";
import AdminHeader from "../components/AdminHeader";
import { Link, useLocation } from "react-router-dom";
import { getAuth, onAuthStateChanged } from "firebase/auth";
import { logUserActivity } from "../utils/logUserActivity";
import AdminSidebar from "../components/AdminSidebar";

function NeonCard({ children }) {
    return (
        <div className="rounded-2xl border border-[#D4FF00]/30 bg-black/60 p-4 shadow-[0_0_40px_rgba(212,255,0,0.1)]">
            {children}
        </div>
    );
}

// ---- helpers for top-level /bookings ----
function parseLocalDateTime(dateStr, timeStr) {
    if (!dateStr || !timeStr) return null;
    const [H, M] = String(timeStr).split(":").map(Number);
    const d = new Date(dateStr);
    if (Number.isFinite(H) && Number.isFinite(M)) d.setHours(H, M, 0, 0);
    else d.setHours(0, 0, 0, 0);
    return d;
}

/** Normalize a booking regardless of schema (Timestamp start/end OR date+startTime/endTime). */
function normalizeBooking(id, data) {
    const status = (data.status || "").toLowerCase();
    // prefer Timestamp fields if present
    const tsStart = data.start?.toDate?.();
    const tsEnd = data.end?.toDate?.();
    let start = tsStart || null;
    let end = tsEnd || null;
    if (!start && data.date) {
        start = parseLocalDateTime(data.date, data.startTime || "00:00");
        end = parseLocalDateTime(data.date, data.endTime || "00:00") || start;
    }
    return {
        id,
        status,
        start,
        end: end || start,
        seatId: data.seatId || data.pcId || data.seat || "—",
        userId: data.userId || data.uid || null,
    };
}

export default function AdminUsers() {
    const [users, setUsers] = useState([]);
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(null);
    const [expanded, setExpanded] = useState(null);
    const [bookingMap, setBookingMap] = useState({});
    const [creditInputs, setCreditInputs] = useState({});
    const [searchQuery, setSearchQuery] = useState("");

    const location = useLocation();

    useEffect(() => {
        const auth = getAuth();
        const unsubscribeAuth = onAuthStateChanged(auth, (user) => {
            if (!user) {
                setUsers([]);
                setLoading(false);
                return;
            }

            const unsub = onSnapshot(collection(db, "users"), (snap) => {
                const userList = snap.docs.map((doc) => ({
                    id: doc.id,
                    ...doc.data(),
                }));
                setUsers(userList);
                setLoading(false);
                setCreditInputs(
                    userList.reduce((acc, user) => {
                        acc[user.id] = ((user.credits || 0) / 100).toFixed(2);
                        return acc;
                    }, {})
                );
            });

            return () => unsub();
        });

        return () => unsubscribeAuth();
    }, []);

    const handleCreditChange = async (userId, newCreditStr, reason = "Manual update") => {
        setSaving(userId);
        try {
            const auth = getAuth();
            const currentAdmin = auth.currentUser;
            if (!currentAdmin) {
                alert("You must be signed in as an admin to perform this action.");
                return;
            }
            const adminEmail = currentAdmin.email;
            const userRef = doc(db, "users", userId);
            const userSnap = await getDoc(userRef);
            if (!userSnap.exists()) throw new Error("User not found");
            const oldCredits = userSnap.data().credits || 0;

            const parsed = parseFloat(newCreditStr);
            const safeValue = Number.isFinite(parsed) ? parsed : 0;
            const newCreditsCents = Math.round(safeValue * 100);

            await updateDoc(userRef, { credits: newCreditsCents });
            await addDoc(collection(userRef, "creditLogs"), {
                timestamp: serverTimestamp(),
                amount: newCreditsCents - oldCredits,
                oldCredits,
                newCredits: newCreditsCents,
                reason,
                admin: adminEmail,
            });
            await logUserActivity(userId, "Credit change", `${reason}: ${newCreditsCents - oldCredits}`);
        } catch (err) {
            alert("Failed to update credits.");
            console.error(err);
        } finally {
            setSaving(null);
        }
    };

    const handleDelete = async (id) => {
        if (!window.confirm("Are you sure you want to delete this user?")) return;
        try {
            await deleteDoc(doc(db, "users", id));
            await logUserActivity(id, "User deleted", "Deleted from admin panel");
        } catch (err) {
            alert("Failed to delete user.");
            console.error(err);
        }
    };

    const toggleExpand = async (uid) => {
        setExpanded((x) => (x === uid ? null : uid));
        if (expanded === uid) return;
        try {
            const next = await fetchNextBookingFromTop(uid); // ← from /bookings
            setBookingMap((prev) => ({ ...prev, [uid]: { next } }));
        } catch (e) {
            console.error("Fetch details failed:", e);
        }
    };

    // search
    const filteredUsers = useMemo(() => {
        const q = searchQuery.trim().toLowerCase();
        if (!q) return users;
        return users.filter((u) => {
            const name = (u.displayName || "player").toLowerCase();
            const email = (u.email || "").toLowerCase();
            const id = (u.id || "").toLowerCase();
            return name.includes(q) || email.includes(q) || id.includes(q);
        });
    }, [users, searchQuery]);

    const navItems = [
        { name: "Site Config", path: "/admin" },
        { name: "User Manager", path: "/admin/users" },
        { name: "Bookings", path: "/admin/bookings" },
        { name: "Revenue", path: "/admin/revenue" },
    ];

    if (loading) return <div className="p-6 text-gray-300">Loading users…</div>;

    return (
        <>
            <AdminHeader />
            <div className="flex flex-col md:flex-row">
                <div className="w-full md:w-48 bg-black border-b md:border-b-0 p-4">
                    <AdminSidebar />
                </div>

                <div className="flex-1 p-4">
                    <div className="max-w-6xl mx-auto text-white">
                        <div className="mb-6">
                            <div className="flex items-center text-[10px] tracking-[0.3em] uppercase text-[#a9ffa2]/70">
                                <span>RGC // Admin Ops Console</span>
                            </div>
                            <h1 className="mt-2 text-3xl md:text-5xl font-extrabold">
                                User <span className="text-accent">Control</span> Room
                            </h1>
                        </div>

                        {/* Search */}
                        <div className="mb-3 flex items-center gap-2">
                            <input
                                type="text"
                                value={searchQuery}
                                onChange={(e) => setSearchQuery(e.target.value)}
                                placeholder="Search by name, email, or UID…"
                                className="w-full md:w-96 px-3 py-2 rounded-lg bg-neutral-900 border border-[#D4FF00]/30 text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-[#D4FF00]"
                            />
                            {searchQuery && (
                                <button
                                    onClick={() => setSearchQuery("")}
                                    className="px-3 py-2 text-xs rounded-lg border border-[#D4FF00]/40 hover:bg-[#D4FF00]/10"
                                >
                                    Clear
                                </button>
                            )}
                            <div className="text-xs text-gray-400 ml-auto">
                                Showing <span className="text-[#D4FF00]">{filteredUsers.length}</span> of {users.length}
                            </div>
                        </div>

                        <div className="overflow-x-auto rounded-2xl border border-[#D4FF00]/20">
                            <table className="min-w-full text-sm">
                                <thead className="bg-neutral-900 text-gray-300">
                                    <tr>
                                        <th className="text-left px-4 py-3">Display Name</th>
                                        <th className="text-left px-4 py-3">Email</th>
                                        <th className="text-left px-4 py-3">Credits ($)</th>
                                        <th className="text-left px-4 py-3">Quick Actions</th>
                                        <th className="text-left px-4 py-3">Created</th>
                                        <th className="text-left px-4 py-3">Actions</th>
                                    </tr>
                                </thead>
                                <tbody className="divide-y divide-white/5">
                                    {filteredUsers.map((u) => (
                                        <React.Fragment key={u.id}>
                                            <tr className="hover:bg-white/5">
                                                <td className="px-4 py-3 font-medium text-white">{u.displayName || "Player"}</td>
                                                <td className="px-4 py-3 text-gray-300">{u.email || "—"}</td>
                                                <td className="px-4 py-3">
                                                    <input
                                                        type="number"
                                                        className="w-24 px-2 py-1 bg-neutral-800 text-white text-right rounded border border-[#D4FF00]/30 focus:ring-2 focus:ring-[#D4FF00] focus:outline-none"
                                                        value={creditInputs[u.id] ?? ""}
                                                        onChange={(e) =>
                                                            setCreditInputs((prev) => ({
                                                                ...prev,
                                                                [u.id]: e.target.value,
                                                            }))
                                                        }
                                                        onBlur={() => handleCreditChange(u.id, creditInputs[u.id])}
                                                        disabled={saving === u.id}
                                                    />
                                                </td>
                                                <td className="p-3 text-right flex justify-start gap-2">
                                                    <button
                                                        className="px-2 py-1 rounded bg-[#D4FF00] text-black text-xs font-semibold hover:brightness-90"
                                                        onClick={() =>
                                                            handleCreditChange(
                                                                u.id,
                                                                ((Number.isFinite(parseFloat(creditInputs[u.id])) ? parseFloat(creditInputs[u.id]) : 0) + 5).toFixed(2),
                                                                "Added $5 via admin panel"
                                                            )
                                                        }
                                                    >
                                                        +$5
                                                    </button>
                                                    <button
                                                        className="px-2 py-1 rounded border border-[#D4FF00]/60 text-xs hover:bg-[#D4FF00]/10"
                                                        onClick={() =>
                                                            handleCreditChange(
                                                                u.id,
                                                                ((Number.isFinite(parseFloat(creditInputs[u.id])) ? parseFloat(creditInputs[u.id]) : 0) - 5).toFixed(2),
                                                                "Removed $5 via admin panel"
                                                            )
                                                        }
                                                    >
                                                        −$5
                                                    </button>
                                                </td>
                                                <td className="px-4 py-3 text-gray-400">
                                                    {u.createdAt?.toDate?.().toLocaleString?.() || "—"}
                                                </td>
                                                <td className="px-4 py-3">
                                                    <div className="flex gap-2">
                                                        <button
                                                            onClick={() => handleDelete(u.id)}
                                                            className="text-red-500 hover:text-red-400 text-xs underline"
                                                        >
                                                            Delete
                                                        </button>
                                                        <button
                                                            onClick={() => toggleExpand(u.id)}
                                                            className="text-gray-300 hover:text-white text-xs underline"
                                                        >
                                                            Details
                                                        </button>
                                                        <button
                                                            className="text-gray-300 hover:text-white text-xs underline"
                                                            onClick={() => window.open(`/admin/users/${u.id}`)}>
                                                            <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24"
                                                                viewBox="0 0 24 24" fill="none" stroke="currentColor"
                                                                stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"
                                                                aria-hidden="true">
                                                                <circle cx="12" cy="12" r="10"></circle>
                                                                <circle cx="12" cy="9" r="3.5"></circle>
                                                                <path d="M7 18a5 5 0 0 1 10 0"></path>
                                                            </svg>
                                                        </button>
                                                    </div>
                                                </td>
                                            </tr>

                                            {expanded === u.id && (
                                                <tr>
                                                    <td colSpan={6} className="px-4 py-3 bg-black/60">
                                                        <div className="grid md:grid-cols-3 gap-4">
                                                            <NeonCard>
                                                                <div className="text-[#D4FF00] font-semibold mb-1">Next booking</div>
                                                                {bookingMap[u.id]?.next ? (
                                                                    <div className="text-sm text-gray-200">
                                                                        <div>
                                                                            Seat:{" "}
                                                                            <span className="font-semibold text-white">
                                                                                {bookingMap[u.id].next.seatId}
                                                                            </span>
                                                                        </div>
                                                                        <div>
                                                                            {bookingMap[u.id].next.start?.toLocaleString?.()} —{" "}
                                                                            {bookingMap[u.id].next.end?.toLocaleString?.()}
                                                                        </div>
                                                                        <div className="text-xs text-gray-400 mt-1">
                                                                            Status: {bookingMap[u.id].next.status}
                                                                        </div>

                                                                        {/* Deep link to bookings page */}
                                                                        <div className="mt-2">
                                                                            <Link
                                                                                to={{
                                                                                    pathname: "/admin/bookings",
                                                                                    search: `?user=${u.id}&booking=${bookingMap[u.id].next.id}`,
                                                                                }}
                                                                                state={{ user: u, booking: bookingMap[u.id].next }}
                                                                                className="underline text-[#D4FF00] hover:text-white text-xs"
                                                                            >
                                                                                Open booking
                                                                            </Link>
                                                                        </div>
                                                                    </div>
                                                                ) : (
                                                                    <div className="text-gray-400 text-sm">None.</div>
                                                                )}
                                                            </NeonCard>

                                                            <NeonCard>
                                                                <div className="text-[#D4FF00] font-semibold mb-1">Latest payment</div>
                                                                <div className="text-gray-400 text-sm">
                                                                    None. {/* no gateway yet */}
                                                                </div>
                                                            </NeonCard>

                                                            <NeonCard>
                                                                <div className="text-[#D4FF00] font-semibold mb-1">Quick links</div>
                                                                <div className="text-sm space-y-1">
                                                                    <Link
                                                                        to={`/admin/users/${u.id}`}
                                                                        className="underline text-gray-200 hover:text-white block"
                                                                    >
                                                                        Open user detail
                                                                    </Link>
                                                                    {bookingMap[u.id]?.next && (
                                                                        <Link
                                                                            to={{
                                                                                pathname: "/admin/bookings",
                                                                                search: `?user=${u.id}&booking=${bookingMap[u.id].next.id}`,
                                                                            }}
                                                                            state={{ user: u, booking: bookingMap[u.id].next }}
                                                                            className="underline text-gray-200 hover:text-white block"
                                                                        >
                                                                            Open upcoming booking
                                                                        </Link>
                                                                    )}
                                                                </div>
                                                            </NeonCard>
                                                        </div>
                                                    </td>
                                                </tr>
                                            )}
                                        </React.Fragment>
                                    ))}

                                    {!loading && filteredUsers.length === 0 && (
                                        <tr>
                                            <td colSpan={6} className="px-4 py-6 text-center text-gray-400">
                                                No users match your search.
                                            </td>
                                        </tr>
                                    )}
                                </tbody>
                            </table>
                        </div>
                    </div>
                </div>
            </div>
        </>
    );
}

/** Fetch earliest future (next) booking for a user from top-level /bookings without needing composite indexes. */
async function fetchNextBookingFromTop(uid) {
    const qRef = query(collection(db, "bookings"), where("userId", "==", uid));
    const snap = await getDocs(qRef);

    let best = null;
    const now = Date.now();

    snap.forEach((d) => {
        const norm = normalizeBooking(d.id, d.data() || {});
        if (!norm.start) return;
        if (norm.status === "canceled") return;
        if (norm.start.getTime() <= now) return;
        if (!best || norm.start < best.start) best = norm;
    });

    return best; // may be null
}
